import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CurrentEnvironment, serviceURL } from './ServiceConstants';
import { createParser, type EventSourceMessage } from 'eventsource-parser';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import { DebugLogger } from '@/DebugLogger';
type CookieName = 'idToken' | 'refreshToken' | 'hasAuthenticated';
function ReadCookie({
  cookieName,
}: {
  cookieName: CookieName;
}): string | undefined {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(cookieName))
    ?.split('=')[1];
}

type AuthData = {
  idToken: string;
  refreshToken: string;
};

function ReadAuthData(): AuthData | null {
  const idToken = ReadCookie({ cookieName: 'idToken' });
  if (idToken === undefined || idToken.length === 0) {
    return null;
  }

  const refreshToken = ReadCookie({ cookieName: 'refreshToken' });
  if (refreshToken === undefined || refreshToken.length === 0) {
    return null;
  }

  // NOTE: We are ignoring the expiry for now, and relying on the server to reject expired refresh tokens.
  return { idToken, refreshToken };
}

function StoreAuthData({ idToken, refreshToken }: AuthData) {
  document.cookie = serializeCookie('idToken', idToken);
  document.cookie = serializeCookie('refreshToken', refreshToken);
}

function StoreHasAuthenticated() {
  document.cookie = serializeCookie('hasAuthenticated', 'true');
}

function ReadHasAuthenticated(): boolean {
  const hasAuthenticated = ReadCookie({ cookieName: 'hasAuthenticated' });
  return hasAuthenticated === 'true';
}

function serializeCookie(
  name: string,
  value: string,
  extraAttributes: string[] = [],
): string {
  function cookieAttributes(): string[] {
    const attributes = ['path=/'];
    if (CurrentEnvironment === 'prod') {
      attributes.push('domain=codifyapp.ai');
    }

    return attributes;
  }

  const attributes = cookieAttributes().concat(extraAttributes);
  return `${name}=${value}; ${attributes.join('; ')}`;
}

function ClearAuthData() {
  const expiredAttributes = [
    'Max-Age=0',
    'expires=Thu, 01 Jan 1970 00:00:00 GMT',
  ];
  document.cookie = serializeCookie('idToken', '', expiredAttributes);
  document.cookie = serializeCookie('refreshToken', '', expiredAttributes);
}

type LoadingState = { isLoading: boolean };

export type AuthenticationState =
  | ({
      type: 'unauthenticated';
      hasJoinedWaitlist?: boolean;
      canAttemptRefresh: boolean;
      hasAuthenticated?: boolean;
      errorMessage: string | null;
    } & LoadingState)
  | ({
      type: 'authenticated';
      token: string;
      refreshToken?: string;
      hasAuthenticated: true;
      errorMessage: string | null;
    } & LoadingState);

type AuthenticationAction =
  | { type: 'log-out' }
  | {
      type: 'authenticate';
      token: string;
      refreshToken?: string;
      expiry?: string;
    }
  | { type: 'has-joined-waitlist' }
  | { type: 'start-load' }
  | { type: 'fail-load' }
  | { type: 'start-refresh' }
  | {
      type: 'refresh-authentication';
      token: string;
      refreshToken?: string;
      expiry?: string;
    }
  | { type: 'mark-has-authenticated' }
  | { type: 'auth-error'; message?: string }
  | { type: 'clear-error' };
type AuthenticationDispatch = (_: AuthenticationAction) => void;

function AuthenticationReducer(
  state: AuthenticationState,
  action: AuthenticationAction,
): AuthenticationState {
  const logger = DebugLogger('[Authentication Reducer] ');
  logger({ message: 'Handling action: ' + action.type });
  function reject(reason: string) {
    logger({ message: 'Keeping existing state because: ' + reason });
  }
  switch (action.type) {
    case 'log-out': {
      ClearAuthData();
      return {
        type: 'unauthenticated',
        canAttemptRefresh: false,
        isLoading: false,
        hasAuthenticated: state.hasAuthenticated,
        errorMessage: null,
      };
    }
    case 'has-joined-waitlist': {
      if (state.type !== 'unauthenticated') {
        return state;
      }

      return {
        ...state,
        isLoading: false,
        hasJoinedWaitlist: true,
      };
    }
    case 'authenticate': {
      if (action.refreshToken !== undefined) {
        StoreAuthData({
          idToken: action.token,
          refreshToken: action.refreshToken,
        });
      }
      StoreHasAuthenticated();

      return {
        isLoading: false,
        type: 'authenticated',
        token: action.token,
        refreshToken: action.refreshToken,
        hasAuthenticated: true,
        errorMessage: null,
      };
    }
    case 'start-load':
      return { ...state, isLoading: true, errorMessage: null };
    case 'fail-load':
      return { ...state, isLoading: false };
    case 'start-refresh': {
      if (state.type === 'authenticated') {
        reject('cannot refresh when already authenticated.');
        return state;
      }

      return {
        ...state,
        isLoading: true,
        canAttemptRefresh: false,
        errorMessage: null,
      };
    }
    case 'refresh-authentication': {
      if (state.type === 'authenticated') {
        reject('cannot refresh when already authenticated.');
        return state;
      }
      if (!state.canAttemptRefresh) {
        reject('cannot refresh when state.canAttemptRefresh is false.');
        return state;
      }

      // Perform the same logic that `reducer` does when given an `authenticate` action.
      return AuthenticationReducer(state, {
        type: 'authenticate',
        token: action.token,
        refreshToken: action.refreshToken,
        expiry: action.expiry,
      });
    }
    case 'mark-has-authenticated': {
      return {
        ...state,
        hasAuthenticated: true,
      };
    }
    case 'auth-error': {
      return {
        ...state,
        errorMessage: action.message ?? 'Something went wrong',
        isLoading: false,
      };
    }
    case 'clear-error': {
      if (state.errorMessage === null) {
        reject('cannot clear-error when state.errorMEssage is null.');
        return state;
      }

      return {
        ...state,
        errorMessage: null,
      };
    }
  }
}

export type SSEEventHandler = {
  onEvent: (_: EventSourceMessage) => void;
  onFinish?: () => void;
  onError?: (_: unknown) => void;
};

function StreamToAsyncIterable<T>(
  stream: ReadableStream<T>,
): AsyncIterableIterator<T> {
  // We have to type erase to `any` to handle the polyfill
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const erased = stream as any;
  if (erased[Symbol.asyncIterator]) {
    return erased;
  }

  const reader = erased.getReader();
  return {
    async next() {
      try {
        const result = await reader.read();
        if (result?.done) {
          reader.releaseLock();
        } // release lock when stream becomes closed
        return result;
      } catch (e) {
        reader.releaseLock(); // release lock when stream becomes errored
        throw e;
      }
    },

    async return() {
      const cancelPromise = reader.cancel();
      reader.releaseLock();
      await cancelPromise;
      return { done: true, value: undefined };
    },

    [Symbol.asyncIterator]() {
      return this;
    },
  };
}

async function handleSSEStream(
  body: ReadableStream<Uint8Array<ArrayBuffer>>,
  eventHandler: SSEEventHandler,
) {
  try {
    const eventStream = body
      .pipeThrough(new TextDecoderStream())
      .pipeThrough(new EventSourceParserStream());
    const eventIter = StreamToAsyncIterable(eventStream);
    for await (const event of eventIter) {
      eventHandler.onEvent(event);
    }

    eventHandler.onFinish?.();
  } catch (error) {
    eventHandler.onError?.(error);
  }
}

const AuthenticationLogger = DebugLogger('[Authentication] ');

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const data = await response.clone().json();
    if (data && typeof data === 'object') {
      for (const key of ['message', 'reason', 'error', 'detail']) {
        const value = (data as Record<string, unknown>)[key];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
      }
    }
  } catch (jsonError) {
    console.debug('Failed to parse error response JSON', jsonError);
    // Ignore JSON parsing errors and fall back to text extraction.
  }

  try {
    const text = await response.clone().text();
    if (text.trim().length > 0) {
      return text.trim();
    }
  } catch (textError) {
    console.debug('Failed to read error response text', textError);
    // Ignore text parsing errors and fall back to a synthesized message.
  }

  return `Request failed with status ${response.status}`;
}

function extractMessageFromUnknown(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return error.trim();
  }

  return 'Network request failed';
}

type AuthErrorKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'server'
  | 'unknown';

function authErrorCopy(kind: AuthErrorKind): string {
  switch (kind) {
    case 'network':
      return 'Unable to connect. Please check your connection and try again.';
    case 'unauthorized':
      return 'Authentication failed. Please check your credentials and try again.';
    case 'forbidden':
      return 'You do not have permission to do that.';
    case 'server':
      return 'Something went wrong on our side. Please try again.';
    case 'unknown':
      return 'Something went wrong. Please try again.';
  }
}

function authErrorKindForStatus(status: number): AuthErrorKind {
  if (status === 401) {
    return 'unauthorized';
  }

  if (status === 403) {
    return 'forbidden';
  }

  if (status >= 500) {
    return 'server';
  }

  return 'unknown';
}

async function internalSendRequest<Req>({
  endpoint,
  method,
  bodyObj,
  requiresAuth,
  authState,
  hasTriedReauth,
  captureAuthError,
  sseEventHandler,
  dispatch,
}: {
  requiresAuth?: boolean | undefined;
  authState: AuthenticationState;
  hasTriedReauth?: boolean;
  captureAuthError?: boolean;
  sseEventHandler?: SSEEventHandler;
  dispatch: AuthenticationDispatch;
} & APIRequest<Req>): Promise<any> {
  AuthenticationLogger({
    message: ` start internalSendRequest\nendpoint: ${endpoint}\n authState: ${authState}`,
  });
  let headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (requiresAuth) {
    if (authState.type === 'unauthenticated') {
      const errorMessage =
        'attempting to access endpoint that requires auth while unauthenticated';
      AuthenticationLogger({ message: errorMessage });
      throw Error(errorMessage);
    }

    headers = { ...headers, Authorization: `Bearer ${authState.token}` };
  }

  const isStreaming = sseEventHandler !== undefined;
  if (isStreaming) {
    headers = { ...headers, Accept: 'text/event-stream' };
  }

  const url = serviceURL({ endpoint });

  const body = bodyObj !== undefined ? JSON.stringify(bodyObj) : undefined;
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body,
    });
    if (isStreaming && response.ok && response.body !== null) {
      handleSSEStream(response.body, sseEventHandler);
      return;
    }
  } catch (error) {
    const message = extractMessageFromUnknown(error);
    dispatch({ type: 'fail-load' });
    if (captureAuthError) {
      dispatch({ type: 'auth-error', message: authErrorCopy('network') });
    }
    AuthenticationLogger({
      message: 'internalSendRequest failed with error: ' + message,
    });
    throw error instanceof Error ? error : new Error(message);
  }

  const requestRequiresReauth =
    requiresAuth && response.status === 403 && endpoint !== refreshEndpoint;
  const canAttemptReauth =
    !hasTriedReauth &&
    authState.type === 'authenticated' &&
    authState.refreshToken !== undefined;
  if (requestRequiresReauth && canAttemptReauth) {
    AuthenticationLogger({
      message: 'Attempt to reauthenticate.',
    });

    try {
      const {
        token,
        refreshToken,
        expiresIn: expiry,
      } = await internalSendRequest({
        endpoint: refreshEndpoint,
        method: 'POST',
        bodyObj: {
          refreshToken: authState.refreshToken,
        },
        requiresAuth: false,
        authState,
        dispatch,
      });
      if (token === authState.token) {
        AuthenticationLogger({
          message: 'Reauthenticating failed: token unchanged.',
        });
        throw new Error('Refreshing authentication failed');
      }

      AuthenticationLogger({
        message: 'Reauthentication succeeded.',
      });

      const authAction: AuthenticationAction = {
        type: 'authenticate',
        token,
        refreshToken,
        expiry,
      };
      dispatch(authAction);
      const refreshedAuthState = AuthenticationReducer(authState, authAction);

      AuthenticationLogger({
        message:
          'retrying request after reauthentication. ' + refreshedAuthState,
      });
      // Retry the original request
      switch (method) {
        case 'POST': {
          return internalSendRequest<Req>({
            endpoint,
            method,
            bodyObj,
            authState: refreshedAuthState,
            requiresAuth,
            hasTriedReauth: true,
            captureAuthError,
            sseEventHandler,
            dispatch,
          });
        }
        case 'GET': {
          return internalSendRequest<Req>({
            endpoint,
            method,
            authState: refreshedAuthState,
            requiresAuth,
            hasTriedReauth: true,
            captureAuthError,
            sseEventHandler,
            dispatch,
          });
        }
      }
    } catch (error) {
      const errorMessage = await extractMessageFromUnknown(error);
      AuthenticationLogger({
        message: 'Reauthenticating failed with error: ' + errorMessage,
      });
      dispatch({ type: 'fail-load' });
      dispatch({ type: 'log-out' });
      throw error;
    }
  }

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response);
    dispatch({ type: 'fail-load' });
    if (captureAuthError) {
      dispatch({
        type: 'auth-error',
        message: authErrorCopy(authErrorKindForStatus(response.status)),
      });
    }
    AuthenticationLogger({
      message: 'internalSendRequest received error response: ' + errorMessage,
    });
    throw new Error(errorMessage);
  }

  return await response.json();
}

const refreshEndpoint = 'refresh_authentication';

export function AuthenticationBoundary({ children }: React.PropsWithChildren) {
  const [state, dispatch] = useReducer(AuthenticationReducer, {
    type: 'unauthenticated',
    isLoading: false,
    canAttemptRefresh: true,
    errorMessage: null,
  });

  async function sendRequest<Req>({
    endpoint,
    method,
    bodyObj,
    requiresAuth,
    captureAuthError,
    sseEventHandler,
  }: {
    requiresAuth?: boolean | undefined;
    captureAuthError?: boolean;
    sseEventHandler?: SSEEventHandler;
  } & APIRequest<Req>): Promise<any> {
    switch (method) {
      case 'POST': {
        return internalSendRequest({
          endpoint,
          method,
          bodyObj,
          requiresAuth,
          authState: state,
          captureAuthError,
          sseEventHandler,
          dispatch,
        });
      }
      case 'GET': {
        return internalSendRequest({
          endpoint,
          method,
          requiresAuth,
          authState: state,
          captureAuthError,
          sseEventHandler,
          dispatch,
        });
      }
    }
  }

  const hasAuthenticated = useMemo(() => {
    return ReadHasAuthenticated();
  }, []);

  useEffect(() => {
    if (hasAuthenticated) {
      dispatch({ type: 'mark-has-authenticated' });
    }
  }, [hasAuthenticated]);

  const authData = useMemo(() => {
    return ReadAuthData();
  }, []);

  useEffect(() => {
    if (authData === null) {
      // No token to authenticate with
      return;
    }

    dispatch({
      type: 'refresh-authentication',
      token: authData.idToken,
      refreshToken: authData.refreshToken,
    });
  }, [authData]);

  function authenticate({
    token,
    refreshToken,
    expiry,
  }: {
    token?: string;
    refreshToken?: string;
    expiry?: string;
  }) {
    if (token === undefined || token === null) {
      dispatch({ type: 'fail-load' });
      dispatch({ type: 'auth-error', message: 'Authentication failed' });
      // Log this error
      return;
    }

    dispatch({ type: 'authenticate', token, refreshToken, expiry });
  }

  async function authenticateThrough<Req>(
    endpoint: 'register' | 'login',
    body: Req,
  ) {
    dispatch({ type: 'start-load' });
    try {
      const {
        token,
        refreshToken,
        expiresIn: expiry,
      } = await sendRequest({
        endpoint,
        method: 'POST',
        bodyObj: body,
        captureAuthError: true,
      });
      authenticate({ token, refreshToken, expiry });
    } catch (error) {
      console.warn(`${endpoint} request failed`, error);
    }
  }

  const service: AuthenticationService = {
    joinWaitlist({ email }) {
      (async () => {
        dispatch({ type: 'start-load' });
        try {
          const {} = await sendRequest({
            endpoint: 'register/waitlist',
            method: 'POST',
            bodyObj: { email },
          });
          dispatch({ type: 'has-joined-waitlist' });
        } catch (error) {
          console.warn('register/waitlist request failed', error);
          dispatch({
            type: 'auth-error',
            message: "Couldn't join the waitlist",
          });
          dispatch({ type: 'fail-load' });
        }
      })();
    },
    register: (body) => {
      void authenticateThrough('register', body);
    },
    login: (body) => {
      void authenticateThrough('login', body);
    },
    sendRequest,
  };
  return (
    <AuthenticationContext.Provider value={{ state, dispatch, service }}>
      {children}
    </AuthenticationContext.Provider>
  );
}

export type APIRequest<Req> =
  | { endpoint: string; method: 'POST'; bodyObj: Req }
  | { endpoint: string; method: 'GET'; bodyObj?: never };

export type AuthenticationService = {
  register: ({ email, password }: { email: string; password: string }) => void;
  login: ({ email, password }: { email: string; password: string }) => void;
  joinWaitlist: ({ email }: { email: string }) => void;
  sendRequest: <Req>({
    endpoint,
    method,
    bodyObj,
    requiresAuth,
    captureAuthError,
    sseEventHandler,
  }: {
    requiresAuth?: boolean | undefined;
    captureAuthError?: boolean;
    sseEventHandler?: SSEEventHandler;
  } & APIRequest<Req>) => Promise<any>;
};

type AuthenticationContextProps = {
  state: AuthenticationState;
  dispatch: AuthenticationDispatch;
  service: AuthenticationService;
};

const AuthenticationContext = createContext<AuthenticationContextProps | null>(
  null,
);

export function useAuthentication(): AuthenticationContextProps {
  const context = useContext(AuthenticationContext);
  if (context === null) {
    throw new Error(
      'useAuthentication must be used within an AuthenticationBoundary',
    );
  }

  return context;
}

export function WhenAuthenticated({ children }: React.PropsWithChildren) {
  const { state } = useAuthentication();

  if (state.type !== 'authenticated') {
    return <></>;
  }

  return <div style={{ display: 'contents' }}>{children}</div>;
}

export function WhenUnauthenticated({ children }: React.PropsWithChildren) {
  const { state } = useAuthentication();

  if (state.type !== 'unauthenticated') {
    return <></>;
  }

  return <div style={{ display: 'contents' }}>{children}</div>;
}
